import { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useDebounce } from "app/custom-hooks/useDebounce";
import { useGetContacts } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetContacts";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { MAX_ATTENDEES } from "app/pages/sales-intelligence/pages/ai-agent/constants";
import {
    getSearchStringErrorVariant,
    mapContactsWithSelectionState,
    mergeSelectedWithSuggestions,
    switchContactSelection,
} from "./utils";
import {
    countSelectedContacts,
    createNewContact,
    parseContactString,
    stringifyContact,
} from "app/pages/sales-intelligence/pages/ai-agent/utils/contactUtils";

const SEARCH_DEBOUNCE_MS = 300;

export const useAttendeesValue = ({ value, onChange }: { value: Contact[]; onChange: (value: Contact[]) => void }) => {
    const {
        params: { domain, id },
    } = useCurrentStateAndParams<{ domain: string; id?: string }>();

    const [debouncedSearch, setSearch, search] = useDebounce<string>("", SEARCH_DEBOUNCE_MS);
    const [highlightedContactId, setHighlightedContactId] = useState<string | undefined>();

    useEffect(() => {
        if (id) {
            setSearch("");
        }
    }, [id, setSearch]);

    const trimmedSearch = debouncedSearch.trim();
    const { data: suggestions, isLoading: isSuggestionsLoading } = useGetContacts({ domain });
    const {
        data: searchResults,
        isFetched: isSearchFetched,
        isFetching: isSearchFetching,
        isError: isSearchError,
    } = useGetContacts({ domain, search: trimmedSearch, enabled: Boolean(trimmedSearch) });

    const contacts = useMemo(() => mergeSelectedWithSuggestions(value, suggestions ?? []), [value, suggestions]);
    const searchOptions = useMemo(
        () => (trimmedSearch && searchResults ? mapContactsWithSelectionState(searchResults, value) : []),
        [trimmedSearch, searchResults, value],
    );

    const isLimitReached = countSelectedContacts(value) >= MAX_ATTENDEES;
    const isSearching = search !== debouncedSearch || (Boolean(trimmedSearch) && isSearchFetching);
    const hasSearchError = Boolean(trimmedSearch) && isSearchError && !isSearching;
    const hasNoSearchResults =
        Boolean(trimmedSearch) && isSearchFetched && !isSearching && !isSearchError && searchOptions.length === 0;
    const errorType = hasNoSearchResults ? getSearchStringErrorVariant(trimmedSearch) : undefined;

    // The only place that enforces MAX_ATTENDEES; the UI just renders the disabled state.
    // Returns true when the contact got selected.
    const toggleContact = useCallback(
        (contact: Contact): boolean => {
            if (contact.isSelected) {
                AIAgentTrackingService.meeting.trackAttendeesCheckboxDeselect();
                onChange(switchContactSelection(value, contact.id));
                return false;
            }

            if (isLimitReached) {
                return false;
            }

            AIAgentTrackingService.meeting.trackAttendeesCheckboxSelect(
                stringifyContact(contact),
                !!contact.isSuggested,
            );
            const isInValue = value.some((c) => c.id === contact.id);
            onChange(isInValue ? switchContactSelection(value, contact.id) : [createNewContact(contact), ...value]);
            return true;
        },
        [value, isLimitReached, onChange],
    );

    const handleCheckboxClick = useCallback(
        (contact: Contact) => {
            toggleContact(contact);
        },
        [toggleContact],
    );

    const handleOptionClick = useCallback(
        (contact: Contact) => {
            if (toggleContact(contact)) {
                setHighlightedContactId(contact.id);
            }
        },
        [toggleContact],
    );

    const clearHighlight = useCallback(() => setHighlightedContactId(undefined), []);

    const handleAddNewContact = useCallback(() => {
        if (isLimitReached) {
            return;
        }

        AIAgentTrackingService.meeting.trackAddNewAttendee(search);
        const newContact = createNewContact(parseContactString(search));
        onChange([newContact, ...value]);

        setSearch("");
        setHighlightedContactId(newContact.id);
    }, [search, value, isLimitReached, onChange, setSearch]);

    return {
        contacts,
        search,
        searchOptions,
        isLimitReached,
        isSearching,
        isSuggestionsLoading,
        hasNoSearchResults,
        hasSearchError,
        errorType,
        highlightedContactId,
        setSearch,
        handleCheckboxClick,
        handleOptionClick,
        handleAddNewContact,
        clearHighlight,
    };
};
